<template>
  <div class="space-y-4">
    <p v-if="error" class="text-red-600">{{ error }}</p>
    <p v-else-if="loading" class="text-gray-500">Comparing…</p>

    <template v-if="rows.length">
      <div class="grid grid-cols-4 gap-4">
        <div v-for="s in summary" :key="s.label" class="rounded-md border border-gray-200 p-4">
          <span class="block text-gray-500">{{ s.label }}</span>
          <span class="text-lg text-gray-900 tabular-nums">{{ s.a }} → {{ s.b }}</span>
          <span :class="['block tabular-nums', s.tone]">{{ s.delta }}</span>
        </div>
      </div>
      <div class="rounded-md border border-gray-200 overflow-hidden">
        <table class="w-full table-fixed">
          <colgroup><col style="width:28%"><col style="width:9%"><col style="width:9%"><col style="width:9%"><col style="width:9%"><col style="width:9%"><col style="width:9%"><col style="width:9%"><col style="width:9%"></colgroup>
          <thead class="bg-gray-50"><tr>
            <th :class="TH">Case</th><th :class="TH">A result</th><th :class="TH">B result</th><th :class="[TH, 'text-right']">A judge</th><th :class="[TH, 'text-right']">B judge</th>
            <th :class="[TH, 'text-right']">A calls</th><th :class="[TH, 'text-right']">B calls</th><th :class="[TH, 'text-right']">A cost</th><th :class="[TH, 'text-right']">B cost</th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="row in rows" :key="row.key" :class="row.changed ? 'bg-primary-50/40' : ''">
              <td :class="TD"><span class="font-medium">{{ row.label }}</span><span v-if="row.changed" class="block text-gray-500">{{ row.changed }}</span></td>
              <td :class="TD"><span :class="tone(row.a)">{{ result(row.a) }}</span></td>
              <td :class="TD"><span :class="tone(row.b)">{{ result(row.b) }}</span></td>
              <td :class="[TD, 'text-right tabular-nums']">{{ judge(row.a) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ judge(row.b) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.a ? row.a.tool_calls : '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.b ? row.b.tool_calls : '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.a ? money(row.a.session_cost_usd) : '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.b ? money(row.b.session_cost_usd) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { API, TD, TH, errorOf, money } from '../hermes';

function avg(list) {
  const v = list.filter((x) => typeof x === 'number');
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
}

export default {
  name: 'TestCompare',
  props: { a: { type: Number, required: true }, b: { type: Number, required: true } },
  data() {
    return { rows: [], summary: [], loading: false, error: '', TD, TH };
  },
  watch: {
    a() { this.compare(); },
    b() { this.compare(); },
  },
  mounted() {
    this.compare();
  },
  methods: {
    money,
    result(d) {
      if (!d) return '—';
      return d.pass ? 'pass' : `${d.passed} / ${d.checks_total}`;
    },
    tone(d) {
      if (!d) return 'text-gray-500';
      return d.pass ? 'text-green-700' : 'text-red-700';
    },
    judge(d) {
      return d && d.judge_score !== null && d.judge_score !== undefined ? `${d.judge_score} / 5` : '—';
    },
    async compare() {
      this.loading = true;
      this.error = '';
      try {
        const [ra, rb] = await Promise.all([
          this.$axios.get(`${API}/runs/${this.a}/results/`, { params: { limit: 100 } }),
          this.$axios.get(`${API}/runs/${this.b}/results/`, { params: { limit: 100 } }),
        ]);
        const A = Object.fromEntries(ra.data.results.map((r) => [r.unit_key, r.data]));
        const B = Object.fromEntries(rb.data.results.map((r) => [r.unit_key, r.data]));
        const keys = [...new Set([...Object.keys(A), ...Object.keys(B)])];
        this.rows = keys.map((key) => {
          const a = A[key] || null;
          const b = B[key] || null;
          let changed = '';
          if (a && b && a.pass !== b.pass) changed = b.pass ? 'now passes' : 'now fails';
          else if (a && b && typeof a.judge_score === 'number' && typeof b.judge_score === 'number' && a.judge_score !== b.judge_score) changed = `judge ${a.judge_score} → ${b.judge_score}`;
          return { key, label: (b || a || {}).label || key, a, b, changed };
        });
        const both = this.rows.filter((r) => r.a && r.b);
        const pct = (list, pick) => (list.length ? Math.round((100 * list.filter(pick).length) / list.length) : 0);
        const num = (x, digits = 2) => (x === null ? '—' : Number(x.toFixed(digits)));
        const delta = (x, y, unit = '', invert = false) => {
          if (x === null || y === null) return { text: '', tone: 'text-gray-500' };
          const d = y - x;
          const good = invert ? d < 0 : d > 0;
          return { text: `${d > 0 ? '+' : ''}${num(d)}${unit}`, tone: d === 0 ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-700' };
        };
        const pa = pct(both, (r) => r.a.pass); const pb = pct(both, (r) => r.b.pass);
        const ja = avg(both.map((r) => r.a.judge_score)); const jb = avg(both.map((r) => r.b.judge_score));
        const ca = avg(both.map((r) => r.a.tool_calls)); const cb = avg(both.map((r) => r.b.tool_calls));
        const $a = both.reduce((s, r) => s + (r.a.session_cost_usd || 0), 0); const $b = both.reduce((s, r) => s + (r.b.session_cost_usd || 0), 0);
        const d1 = delta(pa, pb, '%'); const d2 = delta(ja, jb); const d3 = delta(ca, cb, '', true); const d4 = delta($a, $b, '', true);
        this.summary = [
          { label: `Pass rate over ${both.length} shared cases`, a: `${pa}%`, b: `${pb}%`, delta: d1.text, tone: d1.tone },
          { label: 'Judge average', a: num(ja), b: num(jb), delta: d2.text, tone: d2.tone },
          { label: 'Tool calls per case', a: num(ca, 1), b: num(cb, 1), delta: d3.text, tone: d3.tone },
          { label: 'Session cost', a: money($a), b: money($b), delta: d4.text, tone: d4.tone },
        ];
      } catch (e) {
        this.error = errorOf(e, 'The runs could not be compared.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
